import React, { memo, useMemo } from 'react';
import cn from 'classnames';
import memoize from '@/Framework/dataHelpers/memoize';
import IDeal from '@/finsight/domain/vo/IDeal';
import { Panel } from '@dealroadshow/uikit/core/components/PanelGroup';
import DealHeader from '@/finsight/ui/components/deals/DealsList/DealHeader';
import DealBody from '@/finsight/ui/components/deals/DealsList/DealBody';
import { getRatingsMapping as getRatingsMappingHelper } from '@/finsight/ui/common/helpers';
import keyResolver from '@/Framework/dataHelpers/keyResolver';
import IRatingsMapping from '@/finsight/domain/vo/IRatingsMapping';
import { useDealContext } from '@/finsight/application/Deal/DealContext';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';
import { useDealTooltipContext } from '@/finsight/application/Deal/DealTooltipContext';
import DealHeaderPremiumization from '@/finsight/ui/components/deals/DealsList/DealHeader/DealHeaderPremiumization';
import { UserPlanType } from '@/users/domain/UserPlan';
import CheckUserPlanAccess from '@/finsight/ui/common/components/CheckUserPlanAccess';

import styles from '@/finsight/ui/components/deals/DealsList/dealsList.scss';

interface IDealRowProps {
  dealId: string,
  index: number,
  onDealHeaderClick: (dealId: string) => void,
  showDealDetailsByDefault?: boolean,
  isExpanded?: boolean,
}

const getRatingsMapping = memoize((deal: IDeal, ratingAgenciesMapping) => {
  if (!deal || Object.keys(ratingAgenciesMapping).length === 0) {
    return [];
  }

  let ratingAgenciesForRegion = ratingAgenciesMapping[deal.product.id][deal.country.region.id];

  return getRatingsMappingHelper(deal.tranches, ratingAgenciesForRegion);
// @ts-ignore
}, keyResolver);

const getDeal = (index, deals) => deals[index];

const DealRow = (
  {
    dealId,
    index,
    showDealDetailsByDefault,
    isExpanded = false,
    onDealHeaderClick,
  }: IDealRowProps,
) => {
  const { collection } = useDealContext();
  const {
    dealTooltipOpened,
    setDealTooltipOpened,
  } = useDealTooltipContext();
  const currentDeal = getDeal(index, collection);
  const { ratingAgenciesMap } = useDictionariesContext();
  const ratingsMapping: IRatingsMapping = getRatingsMapping(currentDeal, ratingAgenciesMap);

  const isFirst = index === 0;
  const isLast = index === collection.length - 1;

  const onDealHeaderClickRestricted = (dealId: string) => {
    if (!isFirst) {
      setDealTooltipOpened(dealId);
    } else {
      setDealTooltipOpened(null);
      onDealHeaderClick(dealId);
    }
  };

  const dealHeader = useMemo(() => (
    <CheckUserPlanAccess
      requiredUserPlan={ UserPlanType.PREMIUM }
      fallback={ (
        <DealHeaderPremiumization
          isFirst={ isFirst }
          isLast={ isLast }
          isExpanded={ isExpanded }
          deal={ currentDeal }
          onClick={ onDealHeaderClickRestricted }
        />
      ) }
    >
      <DealHeader
        isFirst={ isFirst }
        isLast={ isLast }
        isExpanded={ isExpanded }
        deal={ currentDeal }
        onClick={ onDealHeaderClick }
      />
    </CheckUserPlanAccess>
  ), [
    currentDeal,
    isFirst,
    isLast,
    isExpanded,
    dealTooltipOpened,
  ]);

  if (!currentDeal) {
    return null;
  }

  return (
    <Panel
      id={ dealId }
      key={ dealId }
      destroyOnToggle
      showArrow={ false }
      isActive={ isExpanded }
      header={ dealHeader }
      className={ cn({ [styles.expandedCard]: isExpanded }, styles.dealListPanelGroupItem) }
      dataTest="dealsList"
    >
      <DealBody
        deal={ currentDeal }
        showDealDetailsByDefault={ showDealDetailsByDefault }
        ratingsMapping={ ratingsMapping }
      />
    </Panel>
  );
};

export default memo(DealRow);
